import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Modal, Table, Form, Input, Spin, message } from 'antd';
import FeatherIcon from 'feather-icons-react';
import { DigitalCardsWrapper } from './style';
import { PageHeader } from '../../components/page-headers/page-headers';
import { Main, TableWrapper, BasicFormWrapper } from '../styled';
import { Button } from '../../components/buttons/buttons';
import { Cards } from '../../components/cards/frame/cards-frame';
import {
  fetchDigitalCards,
  generateDigitalCardAction,
  downloadCardImage,
  downloadCardPDF,
} from '../../redux/digitalCards/actionCreator';
import { getItem } from '../../utility/localStorageControl';

const DigitalCards = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const { digitalCards, total, loading, generating, generatedCard } = useSelector(state => state.digitalCards);

  const [state, setState] = useState({
    pageNumber: 1,
    searchText: '',
    generateModalVisible: false,
    previewModalVisible: false,
    previewCardName: null,
    previewImageUrl: null,
    previewImageLoading: false,
  });

  const {
    pageNumber,
    searchText,
    generateModalVisible,
    previewModalVisible,
    previewCardName,
    previewImageUrl,
    previewImageLoading,
  } = state;

  useEffect(() => {
    dispatch(fetchDigitalCards({ page: pageNumber, searchText }));
  }, [pageNumber, searchText, dispatch]);

  const openPreview = cardName => {
    const user = getItem('user');
    const token = user?.tokens?.access?.token;
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    const url = `${apiEndpoint}/images/card-image/${cardName}`;

    setState(prev => ({
      ...prev,
      previewModalVisible: true,
      previewCardName: cardName,
      previewImageUrl: null,
      previewImageLoading: true,
    }));

    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Error al cargar imagen');
        return res.blob();
      })
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        setState(prev => ({
          ...prev,
          previewImageUrl: objectUrl,
          previewImageLoading: false,
        }));
      })
      .catch(() => {
        message.error('No se pudo cargar la imagen de la tarjeta');
        setState(prev => ({ ...prev, previewImageLoading: false }));
      });
  };

  // When a card is generated, show the preview modal automatically
  useEffect(() => {
    if (generatedCard?.cardname) {
      openPreview(generatedCard.cardname);
    }
    // eslint-disable-next-line
  }, [generatedCard]);

  const handleClosePreview = () => {
    if (state.previewImageUrl) {
      URL.revokeObjectURL(state.previewImageUrl);
    }
    setState(prev => ({ ...prev, previewModalVisible: false, previewCardName: null, previewImageUrl: null }));
  };

  const handleShare = cardName => {
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    const shareUrl = `${apiEndpoint}/images/card-image/${cardName}`;

    if (navigator.share) {
      navigator
        .share({
          title: 'Tarjeta Digital Latido',
          text: `Tu tarjeta digital: ${cardName}`,
          url: shareUrl,
        })
        .catch(() => {});
    } else {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => message.success('Enlace copiado al portapapeles'))
        .catch(() => message.error('No se pudo copiar el enlace'));
    }
  };

  const handleGenerateSubmit = values => {
    dispatch(
      generateDigitalCardAction({
        name: values.name || undefined,
        cellphone: values.cellphone || undefined,
        email: values.email || undefined,
        description: values.description || undefined,
        montoRecarga: values.montoRecarga ? Number(values.montoRecarga) : 0,
      }),
    );
    setState(prev => ({ ...prev, generateModalVisible: false }));
    form.resetFields();
  };

  const columns = [
    {
      title: 'Nombre Tarjeta',
      dataIndex: 'cardname',
      key: 'cardname',
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: val => val || '—',
    },
    {
      title: 'Teléfono',
      dataIndex: 'cellphone',
      key: 'cellphone',
      render: val => val || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: val => val || '—',
    },
    {
      title: 'Monedero',
      key: 'coinpurse',
      render: (_, record) => `$${Number((record.coinpurse || 0) + (record.montoRecarga || 0)).toFixed(2)}`,
    },
    {
      title: 'Fecha Creación',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: val => (val ? new Date(val).toLocaleDateString('es-MX') : '—'),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <div className="action-buttons">
          <Button size="small" type="primary" onClick={() => openPreview(record.cardname)} title="Ver tarjeta">
            <FeatherIcon icon="eye" size={14} />
          </Button>
          <Button size="small" type="default" onClick={() => downloadCardImage(record.cardname)} title="Descargar PNG">
            <FeatherIcon icon="image" size={14} />
          </Button>
          <Button size="small" type="default" onClick={() => downloadCardPDF(record.cardname)} title="Descargar PDF">
            <FeatherIcon icon="file-text" size={14} />
          </Button>
          <Button size="small" type="default" onClick={() => handleShare(record.cardname)} title="Compartir">
            <FeatherIcon icon="share-2" size={14} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        ghost
        title="Tarjetas Digitales"
        buttons={[
          <Button
            key="generate"
            type="primary"
            onClick={() => setState(prev => ({ ...prev, generateModalVisible: true }))}
          >
            <FeatherIcon icon="plus" size={14} />
            Generar Nueva Tarjeta
          </Button>,
        ]}
      />
      <Main>
        <DigitalCardsWrapper>
          <Row gutter={15}>
            <Col xs={24}>
              <Cards headless>
                <TableWrapper className="table-responsive">
                  <Table
                    dataSource={digitalCards}
                    columns={columns}
                    rowKey={record => record.id || record.cardname}
                    loading={loading}
                    pagination={{
                      current: pageNumber,
                      total,
                      pageSize: 10,
                      onChange: page => setState(prev => ({ ...prev, pageNumber: page })),
                      showSizeChanger: false,
                    }}
                  />
                </TableWrapper>
              </Cards>
            </Col>
          </Row>
        </DigitalCardsWrapper>
      </Main>

      {/* Generate Card Modal */}
      <Modal
        title="Generar Nueva Tarjeta Digital"
        visible={generateModalVisible}
        footer={null}
        onCancel={() => {
          setState(prev => ({ ...prev, generateModalVisible: false }));
          form.resetFields();
        }}
      >
        <BasicFormWrapper>
          <Form form={form} layout="vertical" onFinish={handleGenerateSubmit}>
            <Form.Item label="Nombre" name="name">
              <Input placeholder="Nombre del titular (opcional)" />
            </Form.Item>
            <Form.Item label="Teléfono" name="cellphone">
              <Input type="tel" placeholder="Teléfono (opcional)" />
            </Form.Item>
            <Form.Item label="Email" name="email" rules={[{ type: 'email', message: 'Ingresa un email válido' }]}>
              <Input type="email" placeholder="Email (opcional)" />
            </Form.Item>
            <Form.Item label="Descripción" name="description">
              <Input placeholder="Descripción (opcional)" />
            </Form.Item>
            <Form.Item label="Monto Inicial" name="montoRecarga" initialValue={0}>
              <Input type="number" min={0} step="0.01" placeholder="0" />
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit" type="primary" size="large" loading={generating}>
                {generating ? 'Generando...' : 'Generar Tarjeta'}
              </Button>
            </Form.Item>
          </Form>
        </BasicFormWrapper>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title={`Vista previa — ${previewCardName || ''}`}
        visible={previewModalVisible}
        footer={null}
        onCancel={handleClosePreview}
        width={480}
      >
        <div className="preview-image-container" style={{ textAlign: 'center' }}>
          {previewImageLoading ? (
            <Spin size="large" style={{ margin: '40px auto' }} />
          ) : previewImageUrl ? (
            <img
              src={previewImageUrl}
              alt={`Tarjeta ${previewCardName}`}
              style={{ maxWidth: '100%', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', marginBottom: 16 }}
            />
          ) : (
            <p>No se pudo cargar la imagen</p>
          )}
          {previewCardName && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              <Button type="primary" onClick={() => downloadCardImage(previewCardName)}>
                <FeatherIcon icon="download" size={14} />
                Descargar PNG
              </Button>
              <Button type="default" onClick={() => downloadCardPDF(previewCardName)}>
                <FeatherIcon icon="file-text" size={14} />
                Descargar PDF
              </Button>
              <Button type="default" onClick={() => handleShare(previewCardName)}>
                <FeatherIcon icon="share-2" size={14} />
                Compartir
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DigitalCards;
